import { Link } from 'react-router-dom'
import { GraduationCap, ArrowLeft } from 'lucide-react'

export default function Privacy() {
  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-card/80 backdrop-blur-md border-b">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <GraduationCap className="w-8 h-8 text-primary" />
            <span className="text-xl font-bold">
              Nomad<span className="text-[#EF4135]">Lexis</span>
            </span>
          </Link>
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Volver
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className="container mx-auto px-4 py-12 max-w-3xl">
        <h1 className="text-3xl font-bold mb-2">
          Política de Privacidad
        </h1>
        <p className="text-sm text-muted-foreground mb-8">
          Última actualización: febrero 2026
        </p>

        <div className="prose prose-sm max-w-none space-y-6 text-foreground">
          {/* 1 */}
          <section>
            <h2 className="text-xl font-semibold mb-3">
              1. Responsable del tratamiento
            </h2>
            <p className="text-muted-foreground leading-relaxed">
              NomadLexis es responsable del tratamiento de sus datos personales. Para cualquier consulta relativa a la protección de sus datos, puede contactarnos en:
            </p>
            <p className="font-medium mt-2">cfi.nomadlexis@gmail.com</p>
          </section>

          {/* 2 */}
          <section>
            <h2 className="text-xl font-semibold mb-3">
              2. Datos recopilados
            </h2>
            <p className="text-muted-foreground leading-relaxed mb-3">
              Recopilamos los siguientes datos cuando utiliza nuestra plataforma:
            </p>
            <ul className="list-disc pl-6 space-y-1 text-muted-foreground">
              <li>Datos de identificación: nombre, apellido, correo electrónico</li>
              <li>Datos de acceso: contraseña cifrada, historial de conexión</li>
              <li>Datos académicos: progreso en lecciones, resultados de ejercicios, badges obtenidos</li>
              <li>Datos de pago: información procesada por MercadoPago (no almacenamos datos de tarjeta)</li>
            </ul>
          </section>

          {/* 3 */}
          <section>
            <h2 className="text-xl font-semibold mb-3">
              3. Finalidad del tratamiento
            </h2>
            <p className="text-muted-foreground leading-relaxed mb-3">
              Sus datos son utilizados para:
            </p>
            <ul className="list-disc pl-6 space-y-1 text-muted-foreground">
              <li>Brindar acceso a la plataforma y sus funcionalidades</li>
              <li>Realizar seguimiento de su progreso académico</li>
              <li>Procesar pagos y gestionar su suscripción</li>
              <li>Emitir el certificado de finalización del programa</li>
              <li>Enviar notificaciones relevantes sobre su aprendizaje</li>
            </ul>
          </section>

          {/* 4 */}
          <section>
            <h2 className="text-xl font-semibold mb-3">
              4. Base legal
            </h2>
            <p className="text-muted-foreground leading-relaxed">
              El tratamiento de sus datos se basa en su consentimiento, otorgado al registrarse en la plataforma, conforme a la Ley 19.628 sobre Protección de la Vida Privada y la Ley 21.719 de Chile.
            </p>
          </section>

          {/* 5 */}
          <section>
            <h2 className="text-xl font-semibold mb-3">
              5. Compartición de datos
            </h2>
            <p className="text-muted-foreground leading-relaxed mb-3">
              Sus datos pueden ser compartidos con los siguientes proveedores, necesarios para el funcionamiento del servicio:
            </p>
            <ul className="list-disc pl-6 space-y-1 text-muted-foreground">
              <li><strong>Supabase</strong> — Alojamiento de base de datos y autenticación</li>
              <li><strong>MercadoPago</strong> — Procesamiento de pagos</li>
              <li><strong>Vercel</strong> — Alojamiento de la aplicación web</li>
            </ul>
            <p className="text-muted-foreground leading-relaxed mt-3">
              No vendemos ni alquilamos sus datos a terceros.
            </p>
          </section>

          {/* 6 */}
          <section>
            <h2 className="text-xl font-semibold mb-3">
              6. Derechos del usuario
            </h2>
            <p className="text-muted-foreground leading-relaxed mb-3">
              Conforme a la legislación chilena, usted tiene los siguientes derechos:
            </p>
            <ul className="list-disc pl-6 space-y-1 text-muted-foreground">
              <li><strong>Acceso</strong> — Solicitar qué datos personales tenemos sobre usted</li>
              <li><strong>Rectificación</strong> — Corregir datos inexactos o incompletos</li>
              <li><strong>Cancelación</strong> — Solicitar la eliminación de sus datos personales</li>
              <li><strong>Oposición</strong> — Oponerse al tratamiento de sus datos para determinados fines</li>
            </ul>
            <p className="text-muted-foreground leading-relaxed mt-3">
              Para ejercer estos derechos, envíe un correo a cfi.nomadlexis@gmail.com con el asunto "Derechos de datos personales".
            </p>
          </section>

          {/* 7 */}
          <section>
            <h2 className="text-xl font-semibold mb-3">
              7. Seguridad
            </h2>
            <p className="text-muted-foreground leading-relaxed">
              Implementamos medidas de seguridad técnicas y organizativas para proteger sus datos, incluyendo cifrado de contraseñas, conexiones HTTPS y políticas de acceso restringido a la base de datos.
            </p>
          </section>

          {/* 8 */}
          <section>
            <h2 className="text-xl font-semibold mb-3">
              8. Cookies y almacenamiento local
            </h2>
            <p className="text-muted-foreground leading-relaxed">
              Nuestra plataforma utiliza almacenamiento local del navegador (localStorage) para mantener su sesión activa. No utilizamos cookies publicitarias ni rastreadores de terceros.
            </p>
          </section>

          {/* 9 */}
          <section>
            <h2 className="text-xl font-semibold mb-3">
              9. Modificaciones
            </h2>
            <p className="text-muted-foreground leading-relaxed">
              Nos reservamos el derecho de modificar esta política. Cualquier cambio será publicado en esta página con la fecha de actualización correspondiente.
            </p>
          </section>
        </div>
      </main>
    </div>
  )
}
